import { useEffect, useState } from 'react';
import { authHeaders } from './useAnalyticsData';

const API_BASE = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';

// Filtered snapshots can have stale status totals. Use the record summary endpoint
// for explicit scopes, and never infer a breakdown from a paginated record page.
export default function useRecordStatus(data, entity, query = '') {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState({});
  const supplied = data?.statusCounts?.[entity];
  const key = `${entity}|${query}`;
  const filters = new URLSearchParams(query);
  const scoped = ['company', 'sourceUser', 'view'].some((field) => {
    const value = filters.get(field);
    return value != null && value !== 'all' && value.trim() !== '';
  }) || ['customerId', 'sourceCustomerId', 'customerLoadId', 'customerSourceCode'].some((field) => filters.has(field));
  useEffect(() => {
    if (!data || (supplied && !scoped)) return undefined;
    const controller = new AbortController();
    setResult({ key, loading: true });
    const timeout = setTimeout(() => {
      controller.abort();
      setResult({ key, error: 'Status counts took too long to load. Please retry.' });
    }, 30000);
    const params = new URLSearchParams(query);
    params.set('entity', entity);
    params.set('summaryOnly', 'true');
    params.set('limit', '1');
    fetch(`${API_BASE}/api/dashboard/entity-records?${params}`, { signal: controller.signal, headers: authHeaders() })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 404 ? 'Status counts require the updated dashboard API. The overall totals remain available.' : `Could not load status counts (HTTP ${response.status}).`);
        const summary = await response.json();
        const counts = summary.statusCounts;
        if (!counts || Array.isArray(counts) || Object.values(counts).some((count) => typeof count !== 'number' || !Number.isFinite(count) || count < 0)) throw new Error('The server did not return a valid status breakdown.');
        const countTotal = Object.values(counts).reduce((sum, count) => sum + count, 0);
        if (typeof summary.overallTotal === 'number' && countTotal !== summary.overallTotal) throw new Error('The status breakdown does not match the record summary. Refresh the dashboard statistics.');
        if (!scoped) {
          const expected = data[{ Customer: 'totalCustomers', Contact: 'totalContacts', Invoice: 'totalInvoices' }[entity]];
          if (typeof expected === 'number' && countTotal !== expected) throw new Error('The status breakdown does not match the overall total. Refresh the dashboard statistics.');
        }
        if (!controller.signal.aborted) setResult({ key, counts });
      })
      .catch((error) => { if (!controller.signal.aborted) setResult({ key, error: error.message }); })
      .finally(() => clearTimeout(timeout));
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [data, supplied, entity, key, query, retry, scoped]);
  const remoteCounts = result.key === key ? result.counts : undefined;
  const liveCountsRequired = scoped || !supplied;
  const resolvedData = scoped
    ? remoteCounts ? { ...data, statusCounts: { ...data.statusCounts, [entity]: remoteCounts } } : null
    : supplied || !remoteCounts ? data : { ...data, statusCounts: { ...data.statusCounts, [entity]: remoteCounts } };
  return {
    data: resolvedData,
    total: remoteCounts ? Object.values(remoteCounts).reduce((sum, count) => sum + count, 0) : null,
    loading: Boolean(data && liveCountsRequired && (result.key !== key || result.loading)),
    error: liveCountsRequired && result.key === key ? result.error || '' : '',
    retry: () => setRetry((value) => value + 1),
  };
}
